import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { CurrencyFormat } from '@ha/intl';
import { Grid, Typography, Slider, SliderProps, units } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import { currencySymbols } from 'ha/constants/CurrencySymbols';

import { CommonMinMaxNumericInput } from './CommonMinMaxNumericInput';
import { CurrencySelector } from './CurrencySelector';

const useStyles = makeStyles()(t => ({
  controls: {
    marginBottom: units.rem(1),
  },
  priceLabel: {
    paddingTop: units.rem(0.5),
  },
  sliderContainer: {
    marginTop: units.rem(1),
  },
  container: {
    paddingTop: units.rem(1),
    // to compensate for slider thumbs leaving the boundaries
    marginLeft: '6px',
    marginRight: '6px',
  },
  distribution: {
    height: units.rem(2.7),
    width: '100%',
    position: 'relative',
  },
  distributionItem: {
    height: '100%',
    borderTopRightRadius: units.rem(0.1),
    borderTopLeftRadius: units.rem(0.1),
    background: t.palette.grey[300],
  },
}));

const renderDistributionBar = (
  step: {
    range: number;
    value: number;
  },
  index: number,
  distributionMaxValue: number,
  barsCount: number,
  className: string,
): React.ReactNode => (
  <div
    key={index}
    title={step.value.toString()}
    style={{
      width: `${100 / barsCount}%`,
      height: `${(step.value / distributionMaxValue) * 100}%`,
      position: 'absolute',
      bottom: 0,
      left: `${(index * 100) / barsCount}%`,
    }}
  >
    <div className={className} />
  </div>
);

interface Value {
  min: number | null;
  max: number | null;
}

export interface PriceParams {
  priceMin: number;
  priceMax: number;
  priceDistribution: Array<{ range: number; value: number }>;
}

export interface Props extends PriceParams {
  value: Value;
  onChange: (value: Value) => void;
  currencyValue: string;
  onCurrencyChange: (currency: string) => void;
}

const PriceFilter: React.FC<Props> = ({
  value,
  onChange,
  currencyValue,
  onCurrencyChange,
  priceMin,
  priceMax,
  priceDistribution,
}) => {
  const { classes } = useStyles();
  const { T, formatCurrency } = useIntl();

  const [inputValue, setInputValue] = React.useState<Value>({
    min: value.min ?? priceMin,
    max: value.max ?? priceMax,
  });

  const sliderValueMin =
    inputValue.min === null
      ? priceMin
      : Math.max(
          Math.min(
            inputValue.min,
            inputValue.max === null ? priceMax : inputValue.max,
          ),
          priceMin,
        );

  const sliderValueMax =
    inputValue.max === null
      ? priceMax
      : Math.min(
          Math.max(
            inputValue.max,
            inputValue.min === null ? priceMin : inputValue.min,
          ),
          priceMax,
        );

  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      return;
    }

    setInputValue({
      min: value.min ?? priceMin,
      max: value.max ?? priceMax,
    });
  }, [value.min, value.max, priceMin, priceMax]);

  const updateValue = React.useCallback(
    (newValue: Value) => {
      setInputValue(newValue);

      const filterValueMin = newValue.min === priceMin ? null : newValue.min;
      const filterValueMax = newValue.max === priceMax ? null : newValue.max;

      if (
        (newValue.min !== null &&
          newValue.max !== null &&
          newValue.min > newValue.max) ||
        (filterValueMax === value.max && filterValueMin === value.min)
      ) {
        return;
      }

      onChange({
        min: filterValueMin,
        max: filterValueMax,
      });
    },
    [onChange, priceMin, priceMax, value.min, value.max],
  );

  const toVisualPrice = (price: number | null) =>
    price !== null ? Math.floor(price / 100) : '';
  const toCanonicalPrice = React.useCallback(
    (price: string | number) => Math.floor(Number(String(price))) * 100,
    [],
  );

  const onSliderChange = React.useCallback(
    (e: unknown, [min, max]: number[]) => {
      setInputValue({ min, max });
    },
    [],
  );

  const onSliderChangeCommitted = React.useCallback(
    (e: unknown, [min, max]: number[]) => {
      updateValue({ min, max });
    },
    [updateValue],
  );

  const onMinChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        max: inputValue.max,
        min: e.target.value ? toCanonicalPrice(e.target.value) : null,
      });
    },
    [toCanonicalPrice, inputValue.max],
  );

  const onMaxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        min: inputValue.min,
        max: e.target.value ? toCanonicalPrice(e.target.value) : null,
      });
    },
    [toCanonicalPrice, inputValue.min],
  );

  const onMinBlur = React.useCallback(() => {
    updateValue({
      max: inputValue.max,
      min: sliderValueMin,
    });
  }, [inputValue.max, sliderValueMin, updateValue]);

  const onMaxBlur = React.useCallback(() => {
    updateValue({
      min: inputValue.min,
      max: sliderValueMax,
    });
  }, [inputValue.min, sliderValueMax, updateValue]);

  const handleCurrencyChange = React.useCallback(
    (currency: string) => {
      onCurrencyChange(currency);
    },
    [onCurrencyChange],
  );

  const labelFormatter = React.useCallback(
    (price: number): string => {
      const formatted = formatCurrency(
        price,
        currencyValue,
        currencyValue === 'EUR'
          ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
          : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING,
      );

      return price === priceMax ? `${formatted}+` : formatted;
    },
    [currencyValue, priceMax, formatCurrency],
  );

  const distributionValues = priceDistribution.map(step => step.value);
  const distributionMaxValue = distributionValues.reduce(
    (max, current) => Math.max(max, current),
    0,
  );

  const formattedCurrency =
    currencyValue === 'EUR'
      ? currencySymbols[currencyValue] || currencyValue
      : currencyValue;

  const disabled = priceMin === priceMax;

  return (
    <Grid container direction="column">
      <CurrencySelector value={currencyValue} onChange={handleCurrencyChange} />

      <div className={classes.container}>
        {priceDistribution.length ? (
          <Grid container justifyContent="center">
            <div className={classes.distribution}>
              {priceDistribution.map((step, index) =>
                renderDistributionBar(
                  step,
                  index,
                  distributionMaxValue,
                  priceDistribution.length,
                  classes.distributionItem,
                ),
              )}
            </div>
          </Grid>
        ) : null}

        <Grid
          container
          justifyContent="center"
          className={priceDistribution.length ? '' : classes.sliderContainer}
        >
          <Slider
            value={[sliderValueMin ?? priceMin, sliderValueMax ?? priceMax]}
            min={priceMin}
            max={priceMax}
            // default step is 1, which is not enough for ArrowLeft and ArrowRight,
            // since we store actual value in cents, but display as euros (same for other currencies)
            step={100}
            disabled={disabled}
            onChange={onSliderChange as SliderProps['onChange']}
            onChangeCommitted={onSliderChangeCommitted}
            valueLabelDisplay="auto"
            valueLabelFormat={labelFormatter}
            kind="donut"
          />
        </Grid>

        <Grid
          container
          justifyContent="space-between"
          className={classes.priceLabel}
        >
          <Typography.Caption>{labelFormatter(priceMin)}</Typography.Caption>
          {!disabled && (
            <Typography.Caption>{labelFormatter(priceMax)}</Typography.Caption>
          )}
        </Grid>
      </div>

      <CommonMinMaxNumericInput
        id="priceFilter"
        labelMin={T('Minimum price')}
        valueMin={toVisualPrice(inputValue.min)}
        onMinChange={onMinChange}
        onMinBlur={onMinBlur}
        labelMax={T('Maximum price')}
        valueMax={toVisualPrice(inputValue.max)}
        onMaxChange={onMaxChange}
        onMaxBlur={onMaxBlur}
        prefix={formattedCurrency}
      />
    </Grid>
  );
};

export { PriceFilter };
